import React, { Dispatch, useEffect, useRef, useState } from 'react';
import {
  getButtonCoordinates,
  getCharacterCount,
  getChildPages,
  getCurrentWidth,
  getGroupedAssignedPages,
} from '../utils/ConnectedPagesHelperFunctions';
import { Node } from './Node';
import { getHexColor } from '../../../../../types/ICategory';
import handleCopy from '../utils/handleCopy';
import { ConnectedPagesGraphPagination } from './ConnectedPagesGraphPagination';
import { ConnectedPagesGraphChildPages } from './ConnectedPagesGraphChildPages';
import { defaultDimensions } from '../DefaultDimensions';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { IDimensions } from '../interfaces/IConnectedPagesGraphProperties';
import { IConnectedPage } from '../../../../../types/api/connectedPages/IConnectedPage';

type ConnectedPagesGraphCurrentItemProps = {
  current: IConnectedPage;
  currentSet: number;
  containerDimensions: { width: number; height: number };
  dimensions: IDimensions;
  maxPagesPerRow: number;
  radiusOfNodeSphere: number;
  setRadiusOfNodeSphere: Dispatch<React.SetStateAction<number>>;
  connectedPages: IConnectedPage[];
  onPreviousClick: () => void;
  onNextClick: () => void;
  onCustomButtonClick: (targetId?: string | undefined) => void;
  onClickNode: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, url: string) => void;
  activeCustomActionButton?: string;
};

export function ConnectedPagesGraphCurrentItem({
  current,
  currentSet,
  containerDimensions,
  dimensions = defaultDimensions,
  maxPagesPerRow,
  radiusOfNodeSphere,
  setRadiusOfNodeSphere,
  connectedPages,
  onPreviousClick,
  onNextClick,
  onCustomButtonClick,
  onClickNode,
  activeCustomActionButton,
}: ConnectedPagesGraphCurrentItemProps) {
  const { t } = useSonTranslation('ConnectedPagesGraph');

  const [currentSetForConnectedPages, setCurrentSetForConnectedPages] = useState(0);

  const ConnectedPagesGraphCurrentItemFocusable = useRef<HTMLDivElement>(null);
  const focusOnChange = useRef<boolean>(true);

  useEffect(() => {
    if (focusOnChange.current) {
      ConnectedPagesGraphCurrentItemFocusable.current?.focus();
    }

    focusOnChange.current = true;
  });

  useEffect(() => {
    // reset set for activeCustomActionButton, e.g targetId, if we already made clicks
    if (!activeCustomActionButton && currentSetForConnectedPages !== 0) {
      setCurrentSetForConnectedPages(0);
    }
  }, [currentSetForConnectedPages, activeCustomActionButton]);

  const onClickCustomActionButton = (event: React.MouseEvent & { target: { id: string } }) => {
    event.preventDefault();
    const id = event?.target?.id;
    if (id) {
      const isSameTarget = activeCustomActionButton === id;
      onCustomButtonClick(isSameTarget ? undefined : id);
    }

    focusOnChange.current = false;
  };

  const onPrevClicked = (event: React.MouseEvent) => {
    event?.preventDefault();
    onPreviousClick();
  };

  const onNextClicked = (event: React.MouseEvent) => {
    event?.preventDefault();
    onNextClick();
  };

  const nodeDimension = getCurrentWidth(containerDimensions.width, dimensions.currentPage);
  const hasChildPages = current.childPages && current.childPages.length > 0;
  const groupedAssignedPages =
    activeCustomActionButton && current?.groupedAssignedPages && current.groupedAssignedPages.length > 0
      ? getGroupedAssignedPages(
          currentSetForConnectedPages,
          current.groupedAssignedPages,
          maxPagesPerRow,
          activeCustomActionButton
        )
      : null;
  const { coordinatesPreviousButton, coordinatesNextButton } = getButtonCoordinates(
    containerDimensions,
    maxPagesPerRow,
    radiusOfNodeSphere
  );

  return (
    <li className="connected-pages-graph__list-item">
      <Node
        reference={ConnectedPagesGraphCurrentItemFocusable}
        node={current}
        borderColorFromCategories={getHexColor(current.categories)}
        maxMinDimensions={dimensions.currentPage}
        notCurrentPage={current !== connectedPages[0]}
        width={nodeDimension}
        buttonDimensions={{
          width: getCurrentWidth(containerDimensions.width, dimensions.currentPage.button, nodeDimension),
          height: getCurrentWidth(containerDimensions.width, dimensions.currentPage.button, nodeDimension),
        }}
        left={containerDimensions.width / 2}
        top={containerDimensions.width / 2}
        maxNumberOfCharactersForTitleBeforeEllipse={getCharacterCount(
          containerDimensions.width,
          dimensions.currentPage
        )}
        maxNumberOfCharactersForDescriptionTextBeforeEllipse={getCharacterCount(
          containerDimensions.width,
          Object.assign(dimensions.currentPage, dimensions.currentPage.paragraph)
        )}
        activeCustomActionButton={activeCustomActionButton}
        description={current.description}
        groupedAssignedPages={current.groupedAssignedPages}
        onCustomActionButtonClicked={onClickCustomActionButton}
        onCopy={handleCopy}
        ariaLabelPrefix={t('documentTypeLabel')}
      />
      {hasChildPages && !activeCustomActionButton && (
        <ConnectedPagesGraphPagination
          numberOfChildPages={current.childPages ? current.childPages.length : 0}
          currentSetOfPages={currentSet}
          maxPagesPerRow={maxPagesPerRow}
          coordinatesPreviousButton={coordinatesPreviousButton}
          onPreviousButtonClick={onPrevClicked}
          coordinatesNextButton={coordinatesNextButton}
          onNextButtonClick={onNextClicked}
        >
          <ConnectedPagesGraphChildPages
            containerDimensions={containerDimensions}
            ariaLabel={t('childPagesLabel')}
            ariaLabelPrefix={t('documentTypeLabel')}
            childPages={getChildPages(current, currentSet, maxPagesPerRow)}
            radiusToPositionOn={radiusOfNodeSphere}
            maxPagesToRender={maxPagesPerRow}
            onClickHandler={onClickNode}
          />
        </ConnectedPagesGraphPagination>
      )}
      {groupedAssignedPages && groupedAssignedPages.length > 0 && (
        <ConnectedPagesGraphPagination
          numberOfChildPages={groupedAssignedPages.length}
          currentSetOfPages={currentSetForConnectedPages}
          maxPagesPerRow={maxPagesPerRow}
          coordinatesPreviousButton={coordinatesPreviousButton}
          onPreviousButtonClick={() => {
            setRadiusOfNodeSphere(radiusOfNodeSphere);
            setCurrentSetForConnectedPages(currentSetForConnectedPages === 0 ? 0 : currentSetForConnectedPages - 1);
          }}
          coordinatesNextButton={coordinatesNextButton}
          onNextButtonClick={() => {
            setRadiusOfNodeSphere(radiusOfNodeSphere);
            setCurrentSetForConnectedPages(currentSetForConnectedPages + 1);
          }}
        >
          <ConnectedPagesGraphChildPages
            containerDimensions={containerDimensions}
            ariaLabel={t('connectedPagesLabel')}
            ariaLabelPrefix={t('documentTypeLabel')}
            childPages={groupedAssignedPages}
            radiusToPositionOn={radiusOfNodeSphere}
            maxPagesToRender={maxPagesPerRow}
          />
        </ConnectedPagesGraphPagination>
      )}
    </li>
  );
}
